// MA-377 — saveSession outbox wrapper + reconnect drain.
//
// saveSession (src/supabaseClient.js) now THROWS uniformly on every failure
// mode, including a circuit-breaker-open call (it used to be silent there in
// the pattern this mirrors — logAttempt returns false on breaker-open — but
// a silent return here would let a session vanish with no outbox trace and
// no toast). saveSessionWithOutbox's catch therefore covers ALL failure
// paths, not just network/db errors.

import { saveSession as realSaveSession } from "@/supabaseClient.js";
import { isCircuitOpen } from "./networkCircuitBreaker";
import {
  enqueuePendingSession,
  loadPendingSessions,
  removePendingSession,
} from "./localSessionStore";

type SaveSessionFn = (session: Record<string, unknown>) => Promise<void>;

/** Save a session; on ANY failure (breaker-open, network, permanent db
 *  error) queue the payload for the next reconnect drain instead of
 *  dropping it. Returns true iff the cloud write landed on this call. */
export async function saveSessionWithOutbox(
  session: Record<string, unknown> & { studentId: string; runId?: string },
  opts: {
    onCloudFailure?: () => void;
    _saveSession?: SaveSessionFn;
  } = {},
): Promise<boolean> {
  const save = opts._saveSession ?? (realSaveSession as unknown as SaveSessionFn);
  try {
    await save(session);
    return true;
  } catch {
    if (session.runId) enqueuePendingSession(session.studentId, session.runId, session);
    opts.onCloudFailure?.();
    return false;
  }
}

/** Drain queued sessions to the cloud — called from outboxDrain.ts on
 *  reconnect/periodic/foreground ticks. Stops early if the breaker trips
 *  mid-batch; a row stays queued and is retried on the next tick. */
export async function drainPendingSessions(
  opts: { _saveSession?: SaveSessionFn } = {},
): Promise<void> {
  if (isCircuitOpen()) return;
  const save = opts._saveSession ?? (realSaveSession as unknown as SaveSessionFn);
  for (const pending of loadPendingSessions()) {
    if (isCircuitOpen()) break;
    try {
      await save(pending.payload);
      removePendingSession(pending.studentId, pending.runId);
    } catch {
      // stays queued; retried on the next drain tick.
    }
  }
}
